import { push } from '@lagunovsky/redux-react-router'
import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid, Stack } from '@mui/material'
import { compact, groupBy, isEmpty, minBy, orderBy, template, uniq, uniqBy } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import React, { createContext, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import ToastrAction from 'toastr/actions/ToastrAction'
import { LastUpdateCard } from '../../../components/card/LastUpdateCard'
import { CardTitle } from '../../../components/card/NewCard'
import ProgressCircleChart from '../../../components/progress/ProgressCircleChart'
import Tabs from '../../../components/Tabs'
import ConfirmModal from '../../../components/modal/ConfirmModal'
import { DefaultDialogTitle } from '../../../components/styled/Dialog'
import DtoHydrometricStation from '../../../hydrometry/dto/DtoHydrometricStation'
import DtoInstallation from '../../../installation/dto/installation/DtoInstallation'
import DtoPiezometer from '../../../piezometry/dto/DtoPiezometer'
import PiezometerAccessibilitieDto from '../../../piezometry/dto/PiezometerAccessibilitieDto'
import PluviometerDto from '../../../pluviometry/dto/PluviometerDto'
import { getDate } from '../../../utils/DateUtil'
import { formatData } from '../../../utils/ExportDataUtil'
import { getPercentage, hasValue } from '../../../utils/NumberUtil'
import { isTrue } from '../../../utils/SettingUtils'
import { getStationTypeCodeFromType } from '../../../utils/StationUtils'
import { getLabel } from '../../../utils/StoreUtils'
import ExportAction from '../../../export/actions/ExportAction'
import { STATION_TYPE_NAME } from '../../../station/constants/StationConstants'
import { CAMPAIGN_TYPES_CONSTANTS } from '../../constants/CampaignConstants'
import { getPlanningProducers, negIdGenerator, removeObjectivesFromExceptions, removeObjectivesFromPlannings } from '../../utils/CampaignUtils'
import CampaignAction from '../../actions/CampaignAction'
import FormCampaign from './FormCampaign'
import ListStationCampaign from './ListStationCampaign'
import MapCampaignStation from './MapCampaignStation'
import OldSelectStation from './OldSelectStation'
import PopinCampaignObjectives from './station/tab/PopinCampaignObjectives'
import TableCampaignObjectives from './station/tab/TableCampaignObjectives'
import TableCampaignPlannings from './station/tab/TableCampaignPlannings'
import PopinCampaignPlanning from './station/tab/PopinCampaignPlanning'
import StationCampaignExportPopup from './station/StationCampaignExportPopup'
import { useParams } from 'react-router'
import useTitle from '../../../utils/customHook/useTitle'
import useActions from '../../../utils/customHook/useActions'
import useBoolean from '../../../utils/customHook/useBoolean'
import useListIndexed from '../../../utils/customHook/useListIndexed'
import useCampaignBookmarks from '../../../utils/customHook/useCampaignBookmarks'
import UserAction from '../../../administration/components/user/actions/UserAction'

export const StationCampaignContext = createContext({})

const headers = ['code', 'city', 'name', 'contact', 'access', 'order', 'monitoredStation', 'toPlan']
const NO_EXCEPTION = []

const TABS = {
    STATIONS: 'STATIONS',
    OBJECTIVES: 'OBJECTIVES',
    PLANNINGS: 'PLANNINGS',
}

const DashboardCampaign = ({
    accessibilities = [],
    stations,
    stationType,
}) => {
    const {
        campaign,
        campaignStations,
        campaignObjectives,
        stationCampaignPlannings,
        stationCampaignPlanningsExceptions,
        campaignEvents,
        campaignProgress,
        cities,
        contacts,
        accountUser,
    } = useSelector(store => ({
        campaign: store.CampaignReducer.campaign,
        campaignStations: store.CampaignReducer.campaignStations,
        campaignObjectives: store.CampaignReducer.campaignObjectives,
        stationCampaignPlannings: store.CampaignReducer.stationCampaignPlannings,
        stationCampaignPlanningsExceptions: store.CampaignReducer.stationCampaignPlanningsExceptions,
        campaignEvents: store.CampaignReducer.campaignEvents,
        campaignProgress: store.CampaignReducer.campaignProgress,
        cities: store.CityReducer.cities,
        contacts: store.ContactReducer.contacts,
        accountUser: store.AccountReducer.accountUser,
    }), shallowEqual)

    const dispatch = useDispatch()
    const { id } = useParams()

    const [newCampaign, setNewCampaign] = useState(isEmpty(campaign) ? {
        contactCode: accountUser.contactCode,
        administratorCode: accountUser.contributorCode,
        campaignType: CAMPAIGN_TYPES_CONSTANTS.OTHER,
    } : campaign)
    const [selectedStations, setSelectedStations] = useState([])
    const [tmpSelectedStations, setTmpSelectedStations] = useState([])
    const [objectives, setObjectives] = useState([])
    const [plannings, setPlannings] = useState([])
    const [planningsExceptions, setPlanningsExceptions] = useState([])
    const [editMode, setEditMode] = useState(id === 'new')
    const [duplicationMode, setDuplicationMode] = useState(false)
    const [openPopup, setOpenPopup] = useState(false)

    const { value: isStationsInList, setTrue: onDisplayList, setFalse: onDisplayMap } = useBoolean(true)
    const {
        value: isObjectivePopupOpen,
        setTrue: onOpenObjectivePopup,
        setFalse: onCloseObjectivePopup,
    } = useBoolean(false)
    const { value: isExportOpen, setTrue: setExportOpen, setFalse: onCloseExport } = useBoolean(false)
    const [exportPlanningId, setExportPlanningId] = useState()
    const [newPlanning, setNewPlanning] = useState()
    const [pendingCampaignType, setPendingCampaignType] = useState()
    const [formResetKey, setFormResetKey] = useState(0)

    const isEditMode = editMode || duplicationMode

    useTitle(() => (compact([
        {
            title: i18n[stationType],
            href: `/${stationType}`,
        },
        {
            title: i18n.campaigns,
            href: `/${stationType}/campaign`,
        },
        id === 'new' && {
            title: i18n.new,
            href: `/${stationType}/campaign/new/dashboard`,
        },
        duplicationMode && {
            title: i18n.duplicate,
        },
        id !== 'new' && !duplicationMode && {
            title: campaign.name || id,
            href: `/${stationType}/campaign/${id}/dashboard`,
        },
    ])), [id, campaign, duplicationMode])

    const getStationsToMonitor = () => {
        const dateNow = +moment()
        const isOpen = ({ closeDate }) => !closeDate || closeDate > dateNow
        const monitoringByStation = groupBy(campaignEvents.filter(event => event.eventType === 'S' && isOpen(event)), 'stationId')
        const toPlanByStation = groupBy(campaignEvents.filter(event => event.eventType === 'P' && isOpen(event)), 'stationId')
        return stations.map(s => {
            const monitoringEvents = monitoringByStation[s.id] || []
            const toPlanEvents = toPlanByStation[s.id] || []
            return {
                ...s,
                monitoring: monitoringEvents.length,
                monitoringEvents,
                toPlanEventLength: toPlanEvents.length,
                toPlanEvents,
            }
        })
    }

    const stationsToMonitor = useMemo(getStationsToMonitor, [stations, campaignEvents])
    const indexedStationsToMonitor = useListIndexed(stationsToMonitor, 'id')

    const onExport = (exportType) => {
        const data = selectedStations.map(s => {
            const station = indexedStationsToMonitor[s.id] || {}
            const filteredAccessibilities = accessibilities.filter(access => access.idPiezo === station.id)
            const access = filteredAccessibilities.length && filteredAccessibilities.find(a => hasValue(a.order)) ? minBy(filteredAccessibilities, 'order') : (filteredAccessibilities.length ? filteredAccessibilities[0] : null)
            const contact = contacts.find(c => c.id === (access && access.contactCode ? access.contactCode : station.contactCode))
            return {
                ...s,
                code: station.code,
                name: station.name,
                city: station.townCode ? `${getLabel(cities, station.townCode)} [${station.townCode}]` : '',
                monitoredStation: station.monitoringEvents?.map(e => e.comment || e.problem || '').join(', '),
                access: orderBy(filteredAccessibilities, 'order').map(a => a.description).join(', '),
                contact: contact ? [contact.name || '', contact.address, contact.postalCode, getLabel(cities, contact.cityCode), contact.phoneTel || contact.mobile].filter(str => !!str).join(', ') : '',
                toPlan: station.toPlanEvents?.map(e => e.comment || e.problem || '').join(', '),
            }
        })
        const dataWithHeader = data.length > 0 ? [{ ...data[0], headers }, ...data.slice(1)] : []
        dispatch(ExportAction.export(formatData(dataWithHeader), exportType, `${i18n.campaigns}_${i18n[stationType]}_${newCampaign.name || ''}`))
    }

    const onOpenExport = planningId => {
        setExportPlanningId(planningId)
        setExportOpen()
    }

    const formattedStationsToSave = () => uniqBy(selectedStations, 'id').map(station => ({
        id: station.campaignStationId,
        stationId: station.id,
        visitOrder: station.order,
    }))

    const buildFullCampaign = () => ({
        campaign: {
            ...newCampaign,
            beginningApplication: newCampaign.beginningApplication || getDate(+moment()),
            updateLogin: accountUser.login,
            updateDate: +moment(),
            stationType: getStationTypeCodeFromType(stationType),
        },
        stations: formattedStationsToSave(),
        objectives,
        plannings,
        planningsExceptions,
    })

    const onSaveNewCampaign = () => {
        if (!newCampaign.name) {
            dispatch(ToastrAction.error(i18n.thanksToEnterName))
            return
        }
        dispatch(CampaignAction.insertStationFullCampaign(stationType, buildFullCampaign())).then(newId => {
            if (!newId) return
            setEditMode(false)
            setDuplicationMode(false)
            dispatch(push(`/${stationType}/campaign/${newId}/dashboard`))
        })
    }

    const onSaveCampaign = () => {
        dispatch(CampaignAction.updateStationFullCampaign(stationType, id, buildFullCampaign())).then(isSaved => {
            if (!isSaved) return
            dispatch(CampaignAction.fetchCampaign(stationType, id))
            dispatch(CampaignAction.fetchCampaignStation(stationType, id))
            dispatch(CampaignAction.fetchCampaignProgress(stationType, id))
            dispatch(CampaignAction.fetchStationCampaignDetails(stationType, id))
            setEditMode(false)
        })
    }

    const resetFromStore = () => {
        const orderedStations = orderBy(campaignStations.map(({ id: campaignStationId, stationId, visitOrder }) => ({
            id: stationId,
            campaignStationId,
            order: visitOrder,
        })), 'order')
        setSelectedStations(orderedStations)
        setTmpSelectedStations(orderedStations)
        setObjectives(campaignObjectives)
        setPlannings(stationCampaignPlannings)
        setPlanningsExceptions(stationCampaignPlanningsExceptions)
    }

    const onCancel = () => {
        setEditMode(false)
        setDuplicationMode(false)
        setNewCampaign(campaign)
        resetFromStore()
    }

    const onDuplicate = () => {
        setNewCampaign({
            ...campaign,
            name: i18n.copy + campaign.name,
            beginningApplication: undefined,
            endingApplication: undefined,
        })

        const stationIdCorrespondance = {}
        const duplicatedStations = selectedStations.map(station => {
            const newId = negIdGenerator.next().value
            stationIdCorrespondance[station.campaignStationId] = newId
            return { ...station, campaignStationId: newId }
        })
        setSelectedStations(duplicatedStations)
        setTmpSelectedStations(duplicatedStations)

        const objectiveIdCorrespondance = {}
        setObjectives(prev => prev.map(objective => {
            const newId = negIdGenerator.next().value
            objectiveIdCorrespondance[objective.id] = newId
            return { ...objective, id: newId }
        }))

        const planningIdCorrespondance = {}
        setPlannings(prev => prev.map(planning => {
            const newId = negIdGenerator.next().value
            planningIdCorrespondance[planning.id] = newId
            return {
                ...planning,
                id: newId,
                stationsPlan: planning.stationsPlan.map(stationId => stationIdCorrespondance[stationId]),
                objectivesPlan: planning.objectivesPlan.map(objectiveId => objectiveIdCorrespondance[objectiveId]),
            }
        }))

        setPlanningsExceptions(prev => prev.map(exception => ({
            ...exception,
            id: undefined,
            planningId: planningIdCorrespondance[exception.planningId],
            stationsPlan: exception.stationsPlan.map(stationId => stationIdCorrespondance[stationId]),
            objectivesToAddPlan: exception.objectivesToAddPlan.map(objectiveId => objectiveIdCorrespondance[objectiveId]),
            objectivesToDeletePlan: exception.objectivesToDeletePlan.map(objectiveId => objectiveIdCorrespondance[objectiveId]),
        })))

        setDuplicationMode(true)
    }

    useActions(() => {
        if (id === 'new') {
            return {
                save: onSaveNewCampaign,
                cancel: () => dispatch(push(`/${stationType}/campaign`)),
            }
        }
        if (isEditMode) {
            return {
                save: duplicationMode ? onSaveNewCampaign : onSaveCampaign,
                cancel: onCancel,
            }
        }
        const exportAction = {
            exportList: [{ onClick: () => onOpenExport(undefined) }],
        }

        return isTrue(accountUser.consultant) ? exportAction : {
            edit: () => setEditMode(true),
            delete: () => dispatch(CampaignAction.deleteCampaign(stationType, id)),
            duplicate: onDuplicate,
            ...exportAction,
        }
    }, [editMode, duplicationMode, id, newCampaign, selectedStations, objectives, plannings, planningsExceptions, campaignEvents, indexedStationsToMonitor])

    useEffect(() => {
        resetFromStore()
    }, [id, campaignStations, campaignObjectives, stationCampaignPlannings, stationCampaignPlanningsExceptions])

    useEffect(() => {
        if (!isEmpty(campaign)) setNewCampaign(campaign)
    }, [campaign])

    const { getBookmarkAction } = useCampaignBookmarks(stationType)

    useEffect(() => {
        dispatch(UserAction.fetchBookmarks())
    }, [dispatch])

    const onDeleteStation = value => {
        const campaignStationId = selectedStations.find(station => station.id === value.id)?.campaignStationId
        setSelectedStations(prev => prev.filter(station => station.id !== value.id))
        setTmpSelectedStations(prev => prev.filter(station => station.id !== value.id))
        setPlannings(prev => prev.map(planning => ({
            ...planning,
            stationsPlan: planning.stationsPlan.filter(stationId => stationId !== campaignStationId),
        })))
        setPlanningsExceptions(prev => compact(prev.map(exception => {
            const cleaned = { ...exception, stationsPlan: exception.stationsPlan.filter(stationId => stationId !== campaignStationId) }
            return cleaned.stationsPlan.length ? cleaned : undefined
        })))
    }

    const applyCampaignType = campaignType => {
        setObjectives([])
        setPlannings(prev => prev.map(planning => ({ ...planning, objectivesPlan: [] })))
        setPlanningsExceptions(prev => prev.map(exception => ({
            ...exception,
            objectivesToAddPlan: [],
            objectivesToDeletePlan: [],
        })))
        setNewCampaign(prev => ({ ...prev, campaignType }))
    }

    const nbPlanningsWithObjectives = plannings.filter(({ id: planningId, objectivesPlan }) => (
        objectivesPlan.length > 0
        || planningsExceptions.some(exception => exception.planningId === planningId && exception.objectivesToAddPlan.length > 0)
    )).length

    const onChangeCampaign = obj => {
        if (hasValue(obj.campaignType) && obj.campaignType !== newCampaign.campaignType) {
            if (nbPlanningsWithObjectives > 0 || objectives.length > 0) {
                setPendingCampaignType(obj.campaignType)
                return
            }
            applyCampaignType(obj.campaignType)
            return
        }
        if ('contactCode' in obj || 'secondaryContacts' in obj) {
            const nextCampaign = { ...newCampaign, ...obj }
            const contactIds = uniq(compact([nextCampaign.contactCode, ...(nextCampaign.secondaryContacts || [])])).map(code => parseInt(code))
            setPlannings(prev => prev.map(planning => {
                const producers = getPlanningProducers(planning).filter(producer => contactIds.includes(producer))
                return { ...planning, producers, producer: producers[0] }
            }))
        }
        setNewCampaign(prev => ({ ...prev, ...obj }))
    }

    const onCloseSelectStation = () => {
        setOpenPopup(false)
        setTmpSelectedStations(selectedStations)
    }

    const onAddStations = () => {
        setOpenPopup(false)
        setSelectedStations(tmpSelectedStations.map(station => ({
            ...station,
            campaignStationId: station.campaignStationId ?? negIdGenerator.next().value,
        })))
    }

    const onChangeOrder = (stationId, order) => {
        setSelectedStations(prev => prev.map(station => station.id === stationId ? { ...station, order } : station))
    }

    const stationsSelected = selectedStations
        .map(station => ({ ...(indexedStationsToMonitor[station.id] || {}), order: station.order }))
        .filter(station => station.id)

    return (
        <StationCampaignContext.Provider
            value={{
                isEditMode,
                stationType,
                stations,
                campaign: newCampaign, setCampaign: setNewCampaign,
                campaignStations: selectedStations.map(({ campaignStationId, id: stationId, order }) => ({ id: campaignStationId, stationId, visitOrder: order })),
                campaignObjectives: objectives, setCampaignObjectives: setObjectives,
                campaignPlannings: plannings, setCampaignPlannings: setPlannings,
                campaignPlanningsExceptions: planningsExceptions, setCampaignPlanningsExceptions: setPlanningsExceptions,
                onExportPlanning: onOpenExport,
            }}
        >
            <Stack spacing={1}>
                <Grid container spacing={1}>
                    <Grid size={id === 'new' || duplicationMode ? 12 : 9}>
                        <Card>
                            <CardTitle
                                title={i18n.generalInfos}
                                actions={id !== 'new' && !duplicationMode ? [getBookmarkAction(parseInt(id))] : []}
                            />
                            <CardContent>
                                <FormCampaign
                                    key={formResetKey}
                                    campaign={newCampaign}
                                    disabled={{ active: isEditMode, disabled: !isEditMode }}
                                    onChangeCampaign={onChangeCampaign}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                    {id !== 'new' && !duplicationMode && (
                        <Grid size={3}>
                            <Stack spacing={1}>
                                <LastUpdateCard element={newCampaign} withHour />
                                <Card>
                                    <CardTitle title={stationType === STATION_TYPE_NAME.installation && i18n.installationsProgress || i18n.stationsProgress} />
                                    <CardContent>
                                        <ProgressCircleChart
                                            total={campaignProgress.nbStation}
                                            complete={campaignProgress.nbStationValidated}
                                            title={`${getPercentage(campaignProgress.nbStationValidated, campaignProgress.nbStation)} %`}
                                        />
                                    </CardContent>
                                </Card>
                            </Stack>
                        </Grid>
                    )}
                </Grid>
                <Tabs
                    defaultTab={TABS.STATIONS}
                    tabs={[
                        {
                            constant: TABS.STATIONS,
                            label: `${i18n.stations} (${selectedStations.length})`,
                            actions: [
                                { icon: 'note_add', onClick: () => setOpenPopup(true), displayed: isEditMode },
                                { icon: 'map', onClick: onDisplayMap, displayed: isStationsInList },
                                { icon: 'list', onClick: onDisplayList, displayed: !isStationsInList },
                            ],
                        },
                        {
                            constant: TABS.OBJECTIVES,
                            label: `${i18n.objectives} (${objectives.length})`,
                            actions: [
                                { icon: 'note_add', onClick: onOpenObjectivePopup, displayed: isEditMode },
                            ],
                        },
                        {
                            constant: TABS.PLANNINGS,
                            label: `${i18n.plannings} (${plannings.length})`,
                            disabled: id === 'new',
                            actions: [
                                {
                                    icon: 'note_add',
                                    onClick: () => setNewPlanning({ id: negIdGenerator.next().value, stationsPlan: [], objectivesPlan: [], producers: [] }),
                                    displayed: isEditMode,
                                },
                            ],
                        },
                    ]}
                >
                    {activeTab => (
                        <>
                            {activeTab === TABS.STATIONS && isStationsInList && (
                                <ListStationCampaign
                                    campaignId={id}
                                    editMode={isEditMode}
                                    onDeleteStation={onDeleteStation}
                                    accessibilities={accessibilities}
                                    stationType={stationType}
                                    stations={stationsSelected}
                                    onChangeOrder={onChangeOrder}
                                />
                            )}
                            {activeTab === TABS.STATIONS && !isStationsInList && (
                                <MapCampaignStation
                                    selectedStations={selectedStations}
                                    stations={stationsSelected}
                                    stationType={stationType}
                                    accessibilities={accessibilities}
                                    editMode={isEditMode}
                                    onChangeOrder={onChangeOrder}
                                />
                            )}
                            {activeTab === TABS.OBJECTIVES && <TableCampaignObjectives />}
                            {activeTab === TABS.PLANNINGS && <TableCampaignPlannings />}
                        </>
                    )}
                </Tabs>
            </Stack>
            <Dialog fullWidth maxWidth='lg' open={openPopup}>
                <DefaultDialogTitle title={i18n.selectStations} onClose={onCloseSelectStation} />
                <DialogContent>
                    <OldSelectStation
                        stations={stations}
                        selectedStations={tmpSelectedStations}
                        campaignEvents={campaignEvents}
                        stationType={stationType}
                        onChangeSelectedStation={setTmpSelectedStations}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onAddStations} variant='contained' color='primary'>
                        {i18n.validate}
                    </Button>
                </DialogActions>
            </Dialog>
            <ConfirmModal
                isOpen={hasValue(pendingCampaignType)}
                title={i18n.changeCampaignType}
                subTitle={nbPlanningsWithObjectives > 0
                    ? template(i18n.changeCampaignTypeWillClearObjectives)({ nb: nbPlanningsWithObjectives })
                    : i18n.changeCampaignTypeWillClearCampaignObjectives
                }
                onValidate={() => {
                    applyCampaignType(pendingCampaignType)
                    setPendingCampaignType(undefined)
                }}
                onClose={() => {
                    setPendingCampaignType(undefined)
                    setFormResetKey(prev => prev + 1)
                }}
            />
            <PopinCampaignObjectives
                isOpen={isObjectivePopupOpen}
                onClose={onCloseObjectivePopup}
                onValidate={(newObjectives, removedIds) => {
                    setObjectives(newObjectives)
                    if (removedIds.length) {
                        setPlannings(prev => removeObjectivesFromPlannings(prev, removedIds))
                        setPlanningsExceptions(prev => removeObjectivesFromExceptions(prev, removedIds))
                    }
                    onCloseObjectivePopup()
                }}
            />
            {newPlanning && (
                <PopinCampaignPlanning
                    isOpen
                    onClose={() => setNewPlanning(undefined)}
                    onValidate={(planning, exceptions) => {
                        setPlannings(prev => [...prev, planning])
                        setPlanningsExceptions(prev => [
                            ...prev.filter(({ planningId }) => planningId !== planning.id),
                            ...exceptions,
                        ])
                        setNewPlanning(undefined)
                    }}
                    campaignPlanning={newPlanning}
                    campaignPlanningExceptions={NO_EXCEPTION}
                />
            )}
            {isExportOpen && (
                <StationCampaignExportPopup
                    onClose={onCloseExport}
                    onExportCampaign={onExport}
                    planningId={exportPlanningId}
                />
            )}
        </StationCampaignContext.Provider>
    )
}

DashboardCampaign.propTypes = {
    accessibilities: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.instanceOf(PiezometerAccessibilitieDto),
    ])),
    stations: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.instanceOf(PluviometerDto)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoPiezometer)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoHydrometricStation)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoInstallation)),
    ]),
    stationType: PropTypes.oneOf(['piezometry', 'pluviometry', 'hydrometry', 'installation']),
}

export default DashboardCampaign
